/**
 * Admin — Revenue Calculation.
 *
 * Card 1241 built the four-region shell, 1242 filled the KPI row and filters, and 1243
 * fills the product economics table. Cards 1244–1245 fill the remaining two regions.
 *
 * 🔑 A BLOCKED INPUT RENDERS `— / Missing` — NEVER $0, NEVER 0%, NEVER 100%. The rule is
 * not invented in the view: the economics API returns `{ value: null, blockedBy: 'Q3' }`
 * for an unratified input, so the page renders null AS missing and lets the Q-code explain
 * itself. It lives once, in utils/revenueMetrics.js, so no card formats it a second way.
 *
 * 🔑 THE URL IS THE FILTER STATE. `useSearchParams` is the single source of truth, so a
 * copied URL reproduces the exact view and a reload keeps range + filters intact.
 *
 * 🔑 DATE MATH IS NOT REIMPLEMENTED HERE. The range comes from the shared
 * `AdminFiltersModal` `dateRange` control and resolves through `utils/dateRangePresets.js`
 * (ET wall-clock, DST-correct). No second date implementation exists.
 *
 * 🔑 `SKELETON_MIN_MS` is RACED against the fetch, not deleted — `Promise.all([...fetches,
 * settle])` — so a fast response cannot flash the skeleton for a single frame.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminFiltersModal, {
  FiltersButton,
  FilterChips,
  buildFilterChips,
  countActiveFilters,
  removeFilterValue
} from '../../components/admin/AdminFiltersModal';
import RevenueKpiRow from '../../components/admin/RevenueKpiRow';
import RevenueStoredEconomicsRow from '../../components/admin/RevenueStoredEconomicsRow';
import RevenueFirstOrderValueRow from '../../components/admin/RevenueFirstOrderValueRow';
import RevenueProductTable from '../../components/admin/RevenueProductTable';
import RevenueBlockersPanel from '../../components/admin/RevenueBlockersPanel';
import RevenueReconciliationPanel from '../../components/admin/RevenueReconciliationPanel';
import RevenueAssumptionsPanel from '../../components/admin/RevenueAssumptionsPanel';
import RevenueFovReconciliationPanel from '../../components/admin/RevenueFovReconciliationPanel';
import MonthlyCloseoutTable from '../../components/admin/MonthlyCloseoutTable';
import Section from '../../components/admin/RevenueSection';
import { REGIONS } from './revenueCalculationRegions';
import { resolveDateRange } from '../../utils/dateRangePresets';
import { filterCatalogRows } from '../../utils/revenueMetrics';
import { buildCloseoutWindows, fetchCloseoutRows } from '../../utils/monthlyCloseoutModel';
import { revenueCalculationApi } from '../../services/api';
import styles from './RevenueCalculationPage.module.css';

/** Minimum time the skeleton stays up, in ms. Prevents a one-frame skeleton flash. */
export const SKELETON_MIN_MS = 400;

/**
 * The page regions, in render order. EXTRACTED to revenueCalculationRegions.js by card
 * 3065 — this file was already 317 lines, over the 300 standard, before that card, and a
 * fifth region did not fit its ~320 cap. RE-EXPORTED so any importer keeps working.
 */
// Imported for local use AND re-exported. ⛔ `export { X } from '...'` alone does NOT
// bind X in this scope — it forwards it. The JSX below calls REGIONS.map, so the bare
// re-export compiled clean and would have thrown at runtime.
export { REGIONS };

const PROVIDER_OPTIONS = [
  { value: 'DrCare', label: 'DrCare' },
  { value: 'DoctorNow', label: 'DoctorNow' }
];

const ACTIVE_OPTIONS = [
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' }
];

const ECONOMICS_OPTIONS = [
  { value: 'incomplete', label: 'Missing or incomplete economics' },
  { value: 'missing', label: 'Missing only' },
  { value: 'conflicting', label: 'Conflicting only' },
  { value: 'estimated', label: 'Estimated' },
  { value: 'verified', label: 'Verified' }
];

/** URL params owned by the Filters experience. The URL is the source of truth. */
const SELECT_FILTER_PARAMS = ['provider', 'category', 'active', 'economics', 'blocker'];

/** PURE. URL → filter values. */
export const readFiltersFromParams = (searchParams) => {
  const values = {};
  SELECT_FILTER_PARAMS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) values[key] = value;
  });
  const preset = searchParams.get('preset');
  if (preset) {
    values.date = {
      preset: preset.toUpperCase(),
      start: searchParams.get('from') || '',
      end: searchParams.get('to') || ''
    };
  }
  return values;
};

/** PURE. Filter values → URL. Empty values are omitted, so a cleared filter leaves no trace. */
export const writeFiltersToParams = (values) => {
  const params = new URLSearchParams();
  SELECT_FILTER_PARAMS.forEach((key) => {
    if (values?.[key]) params.set(key, values[key]);
  });
  if (values?.date?.preset) {
    params.set('preset', values.date.preset);
    if (values.date.start) params.set('from', values.date.start);
    if (values.date.end) params.set('to', values.date.end);
  }
  return params;
};

const RevenueCalculationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [catalogRows, setCatalogRows] = useState(null);
  const [catalogCounts, setCatalogCounts] = useState(null);
  const [blockers, setBlockers] = useState(null);
  const [assumptions, setAssumptions] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [closeoutRows, setCloseoutRows] = useState(null);

  // URL is the single source of truth for filter state
  const filters = useMemo(() => readFiltersFromParams(searchParams), [searchParams]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    const values = readFiltersFromParams(searchParams);
    const range = resolveDateRange(values.date);
    // Raced, not awaited in sequence — a fast response must not flash the skeleton.
    const settle = new Promise((resolve) => { setTimeout(resolve, SKELETON_MIN_MS); });
    try {
      const [summaryRes, productsRes, blockersRes, reconRes, assumptionsRes] = await Promise.all([
        revenueCalculationApi.getSummary({ dateFrom: range?.from, dateTo: range?.to }),
        revenueCalculationApi.getProducts({ providerType: values.provider, includeInactive: 'true' }),
        revenueCalculationApi.getBlockers(),
        revenueCalculationApi.getReconciliation({ dateFrom: range?.from, dateTo: range?.to }),
        revenueCalculationApi.getAssumptions(),
        settle
      ]);
      setSummary(summaryRes);
      setCatalogRows(productsRes?.rows ?? []);
      setCatalogCounts({ count: productsRes?.count, productCount: productsRes?.productCount });
      setBlockers(blockersRes);
      setReconciliation(reconRes);
      setAssumptions(assumptionsRes);
    } catch (error) {
      await settle;
      setSummary(null);
      setCatalogRows(null);
      setCatalogCounts(null);
      setBlockers(null);
      setReconciliation(null);
      setAssumptions(null);
      setLoadError(error?.message || 'Failed to load revenue calculation');
    } finally {
      setIsLoading(false);
    }
  }, [searchParams]);

  useEffect(() => { load(); }, [load]);

  /**
   * Card 4939. A FIXED Jan–Aug 2026 closeout, so it loads once on mount and is deliberately NOT
   * re-fetched when the page's date filter changes — it answers "how did each month close", not
   * "what is in the current range". It keeps its OWN state because its eight windows cost ~8 s
   * in parallel, and a slow closeout must never delay or blank the five region fetches beside it.
   */
  const closeoutWindows = useMemo(() => buildCloseoutWindows(), []);

  useEffect(() => {
    let cancelled = false;
    fetchCloseoutRows(revenueCalculationApi.getSummary, closeoutWindows)
      .then((rows) => { if (!cancelled) setCloseoutRows(rows); });
    return () => { cancelled = true; };
  }, [closeoutWindows]);

  const scopedRows = useMemo(() => filterCatalogRows(catalogRows, filters), [catalogRows, filters]);

  // Options come from the live row-set, so the filter can never offer an empty category.
  const categoryOptions = useMemo(
    () => [...new Set((catalogRows ?? []).map((row) => row?.category).filter(Boolean))]
      .sort()
      .map((name) => ({ value: name, label: name })),
    [catalogRows]
  );

  const filterDefs = useMemo(
    () => [
      { key: 'date', label: 'Date', type: 'dateRange' },
      { key: 'provider', label: 'Provider', type: 'select', placeholder: 'All providers', options: PROVIDER_OPTIONS },
      { key: 'category', label: 'Category', type: 'select', placeholder: 'All categories', options: categoryOptions },
      { key: 'active', label: 'Active status', type: 'select', placeholder: 'Active and inactive', options: ACTIVE_OPTIONS },
      { key: 'economics', label: 'Economics', type: 'select', placeholder: 'Any data quality', options: ECONOMICS_OPTIONS }
    ],
    [categoryOptions]
  );

  const filterChips = useMemo(() => buildFilterChips(filterDefs, filters), [filterDefs, filters]);

  const selectBlocker = (id) =>
    setSearchParams(writeFiltersToParams({ ...filters, blocker: id ?? '' }));

  const applyFilters = (nextValues) => {
    setSearchParams(writeFiltersToParams(nextValues));
    setFiltersOpen(false);
  };

  /**
   * The single child for a region, or null so `Section` falls through to its empty state.
   * ⛔ Do NOT inline these as sibling JSX expressions — `children` would become an array,
   * which is never nullish, and every region would lose `children ?? <SectionEmpty>`.
   */
  const renderRegionChild = (region) => {
    if (region.id === 'kpi-row' && summary?.orders) {
      return (
        <div data-testid="kpi-row-regions">
          <RevenueStoredEconomicsRow stored={summary.storedEconomics} />
          <p className={styles.sectionLabel} data-testid="catalog-derived-label">
            Catalog-derived order columns · a DIFFERENT population
          </p>
          <p className={styles.sectionNote} data-testid="catalog-derived-basis">
            Summed from the stored <code>orders.*</code> cost columns over the status-filtered
            population (cancelled, denied and pending excluded; the reporting exclusion is NOT
            applied). ⛔ Do not read these against the stored-economics figures above — they
            measure different sets of orders on purpose, and the difference is itemised in the
            reconciliation panel.
          </p>
          <RevenueKpiRow orders={summary.orders} truncated={Boolean(summary.truncated)} scopeNote={scopeNote} />
          <RevenueFirstOrderValueRow firstOrderValue={summary.firstOrderValue} />
        </div>
      );
    }
    if (region.id === 'blockers-panel' && blockers?.blockers?.length > 0) {
      return (
        <RevenueBlockersPanel
          blockers={blockers.blockers}
          exposure={blockers.exposure}
          sourceDoc={blockers.sourceDoc}
          answered={blockers.answered}
          selectedBlocker={filters.blocker ?? null}
          onSelectBlocker={selectBlocker}
        />
      );
    }
    if (region.id === 'assumptions-panel' && assumptions?.assumptions?.length > 0) {
      return <RevenueAssumptionsPanel assumptions={assumptions} />;
    }
    if (region.id === 'reconciliation-panel' && reconciliation?.surfaces?.length > 0) {
      return (
        <>
          <RevenueReconciliationPanel reconciliation={reconciliation} />
          <RevenueFovReconciliationPanel firstOrderValue={reconciliation.firstOrderValue} />
        </>
      );
    }
    if (region.id === 'monthly-closeout' && closeoutRows?.length > 0) {
      return <MonthlyCloseoutTable rows={closeoutRows} />;
    }
    if (region.id === 'product-table' && scopedRows.length > 0) {
      return (
        <RevenueProductTable
          rows={scopedRows}
          apiCount={catalogCounts?.count}
          apiProductCount={catalogCounts?.productCount}
        />
      );
    }
    return null;
  };

  const scopeNote = `${Number(summary?.orders?.orderCount ?? 0)} orders in range · catalog scope `
    + `${scopedRows.length} of ${(catalogRows ?? []).length} product/term rows`;

  return (
    <div className={styles.container} data-testid="revenue-calculation-page">
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Revenue Calculation</h1>
          <p className={styles.subtitle}>
            Product-level revenue, expense and margin. Inputs that are missing or in
            conflict are rendered as blockers — this page never guesses a cost and
            never reports an unknown as zero.
          </p>
        </div>
        <FiltersButton activeCount={countActiveFilters(filters)} onClick={() => setFiltersOpen(true)} />
      </header>

      <FilterChips chips={filterChips} onRemove={(chip) => applyFilters(removeFilterValue(filters, chip))} />

      {loadError && <p className={styles.error} data-testid="load-error">{loadError}</p>}

      <div className={styles.regions}>
        {REGIONS.map((region) => (
          <Section key={region.id} region={region}
            isLoading={isLoading || (region.id === 'monthly-closeout' && closeoutRows === null)}>
            {renderRegionChild(region)}
          </Section>
        ))}
      </div>

      <AdminFiltersModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filterDefs}
        values={filters}
        onApply={applyFilters}
        onReset={() => applyFilters({})}
      />
    </div>
  );
};

export default RevenueCalculationPage;
